// src/pages/AppsPage.tsx — Phase 1 Slice B commit 6; personalised 2026-11-04.
//
// Grouped apps grid. Four sections (Daily / For you / Manage / App Links)
// each render a grid of AppTile cards. The Manage group is filtered by perm
// (admin / news-admin / alerts-admin) and is empty for a regular volunteer.
//
// Personalisation (per user, stored in hub_users.preferences):
//   * Pin bay  -- 4 slots at the top. The pin on any card fills a free slot;
//                 unpin from the bay. One slot can be marked "Show on Home":
//                 that app's launcher renders on the Home page under the
//                 banner (HomeAppLauncher).
//   * Sort     -- every group is alphabetical by default; "Recent" puts the
//                 apps launched most recently first, then the rest A–Z.
//                 Launches are stamped via POST /api/me/app-launch.
//
// Routing:
//   - Internal routes (start with "/") → navigate(route).
//   - External routes (start with "https://") → window.open in new tab.
//
// Permission gating follows STATUS §3 (2026-05-14): me.isAdmin is the sole
// admin gate; me.isNewsAdmin / me.isAlertsAdmin remain as per-resource
// sub-flags so a volunteer granted edit access to a single resource still
// sees that one tile. permissionContextFromMe (lib/apps.ts) builds the
// context so Home resolves the same visibility.

import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCurrentUser, type AppSortKey } from "../auth";
import { C, F } from "../theme";
import { AppTile, PinBay, Section, SearchInput, TopBar } from "../components/ui";
import { FeaturedFirstAiderTile } from "../components/firstAider/FeaturedFirstAiderTile";
import {
  APPS,
  isAppVisible,
  isExternalRoute,
  permissionContextFromMe,
  type AppEntry,
  type AppGroup,
} from "../lib/apps";
import {
  APP_SORT_LABELS,
  PIN_SLOTS,
  readAppSort,
  readHomeApp,
  readPins,
  recordLaunch,
  saveAppSort,
  savePins,
  sortApps,
} from "../lib/appPrefs";

const GROUPS: ReadonlyArray<{ id: AppGroup; title: string; sub: string; cols: number }> = [
  { id: "daily",     title: "Daily",     sub: "Today's essentials",       cols: 2 },
  { id: "for-you",   title: "For you",   sub: "Your stuff",               cols: 2 },
  { id: "manage",    title: "Manage",    sub: "Admin & editors",          cols: 2 },
  { id: "app-links", title: "App Links", sub: "Other NYMR apps",          cols: 4 },
];

const SORT_KEYS: ReadonlyArray<AppSortKey> = ["alpha", "recent"];

export function AppsPage() {
  const navigate = useNavigate();
  const { me } = useCurrentUser();

  const perms = useMemo(() => permissionContextFromMe(me), [me]);
  const visible = useMemo(() => APPS.filter((a) => isAppVisible(a, perms)), [perms]);
  const visibleById = useMemo(() => new Map(visible.map((a) => [a.id, a])), [visible]);

  // ── per-user state (seeded from /api/me, then edited optimistically) ──────
  const [sort, setSort] = useState<AppSortKey>("alpha");
  const [pins, setPins] = useState<string[]>([]);
  const [homeApp, setHomeApp] = useState<string | null>(null);
  const [lastUsed, setLastUsed] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    if (!me) return;
    setSort(readAppSort(me.preferences));
    setPins(readPins(me.preferences));
    setHomeApp(readHomeApp(me.preferences));
    setLastUsed(me.preferences?.appLastUsed ?? {});
  }, [me]);

  // Live app search — instantly filters the visible tiles by name/description.
  const [query, setQuery] = useState("");
  const q = query.trim().toLowerCase();
  const searching = q.length > 0;
  const filtered = searching
    ? visible.filter(
        (a) => a.name.toLowerCase().includes(q) || (a.desc ?? "").toLowerCase().includes(q),
      )
    : visible;

  function openApp(app: AppEntry) {
    // Stamp "last used" first (fire-and-forget) and reflect it locally so a
    // Recent sort re-orders immediately when the user comes back.
    recordLaunch(app.id);
    setLastUsed((prev) => ({ ...prev, [app.id]: new Date().toISOString() }));
    if (isExternalRoute(app.route)) {
      // Slice G — every external tile opens in a new tab with rel-noopener
      // so the destination can't grab the opener window. Previously Live
      // Trains used window.location.href which navigated away from the hub.
      window.open(app.route, "_blank", "noopener,noreferrer");
    } else {
      navigate(app.route);
    }
  }

  async function persistPins(nextPins: string[], nextHome: string | null) {
    const prevPins = pins;
    const prevHome = homeApp;
    setPins(nextPins);
    setHomeApp(nextHome);
    setSaving(true);
    setSaveError(null);
    try {
      await savePins(nextPins, nextHome);
    } catch {
      setPins(prevPins);
      setHomeApp(prevHome);
      setSaveError("Couldn't save your pins. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  function togglePin(app: AppEntry) {
    if (pins.includes(app.id)) {
      const next = pins.filter((p) => p !== app.id);
      void persistPins(next, homeApp === app.id ? null : homeApp);
      return;
    }
    if (pins.length >= PIN_SLOTS) return;
    void persistPins([...pins, app.id], homeApp);
  }

  function changeSort(next: AppSortKey) {
    if (next === sort) return;
    const prev = sort;
    setSort(next);
    saveAppSort(next).catch(() => {
      setSort(prev);
      setSaveError("Couldn't save the sort order. Please try again.");
    });
  }

  const bayFull = pins.length >= PIN_SLOTS;
  // Pins whose app is no longer visible to this user (grant withdrawn) render
  // as empty slots; they are dropped from the saved list on the next pin edit.
  const bayApps = pins.map((id) => visibleById.get(id) ?? null);

  return (
    <div>
      <TopBar title="Apps" sub="Everything available to you" />
      <div style={{ padding: "18px 16px 24px", display: "flex", flexDirection: "column", gap: 22 }}>
        {/* Pin bay — 4 slots, per user. Hidden while searching so results read
            as a clean list. */}
        {!searching && (
          <div>
            <Section
              title="Pinned"
              sub="Your bay"
              right={
                <span style={{ fontFamily: F.body, fontSize: 12, fontWeight: 700, color: C.inkMute, whiteSpace: "nowrap" }}>
                  {pins.length}/{PIN_SLOTS} · Star one to show on Home
                </span>
              }
            />
            <PinBay
              slots={PIN_SLOTS}
              apps={bayApps}
              homeAppId={homeApp}
              busy={saving}
              onOpen={openApp}
              onUnpin={togglePin}
              onSetHome={(app) => void persistPins(pins, app ? app.id : null)}
            />
          </div>
        )}

        {saveError && (
          <div
            role="alert"
            style={{
              padding: 12,
              borderRadius: 12,
              background: C.signalBg,
              border: "1px solid rgba(184,58,43,0.22)",
              color: C.signalDk,
              fontFamily: F.body,
              fontWeight: 700,
              fontSize: 13,
            }}
          >
            {saveError}
          </div>
        )}

        {/* Live app search + sort toggle (per user: A–Z default, or Recent). */}
        <div style={{ display: "flex", gap: 10, alignItems: "stretch" }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <SearchInput value={query} onChange={setQuery} placeholder="Search apps…" />
          </div>
          <div
            role="group"
            aria-label="Sort apps"
            style={{
              display: "flex",
              borderRadius: 14,
              border: `1px solid ${C.hairline}`,
              background: C.card,
              overflow: "hidden",
              flexShrink: 0,
            }}
          >
            {SORT_KEYS.map((key) => {
              const active = sort === key;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => changeSort(key)}
                  aria-pressed={active}
                  title={key === "alpha" ? "Alphabetical" : "Last accessed first, then alphabetical"}
                  style={{
                    minWidth: 64,
                    minHeight: 44,
                    padding: "0 14px",
                    border: "none",
                    background: active ? C.brassDk : "transparent",
                    color: active ? C.brassGlow : C.inkSoft,
                    fontFamily: F.body,
                    fontWeight: 800,
                    fontSize: 13,
                    letterSpacing: 0.2,
                    cursor: "pointer",
                    WebkitTapHighlightColor: "transparent",
                    touchAction: "manipulation",
                  }}
                >
                  {APP_SORT_LABELS[key]}
                </button>
              );
            })}
          </div>
        </div>

        {/* Phase 8 — featured red "Call to First Aiders" tile. Now a User Access
            grant (apps.firstAiderCallout, OFF by default): shown only to admins
            or users who hold the grant, AND only while the system feature flag
            is on. `features.firstAiderCallout !== false` keeps the system gate
            lenient across a stale /api/me; the per-user grant must be explicit.
            Hidden while searching so the results read as a clean app list. */}
        {!searching
          && me?.features?.firstAiderCallout !== false
          && (me?.role === "admin" || me?.apps?.firstAiderCallout === true)
          && <FeaturedFirstAiderTile />}
        {GROUPS.map((group) => {
          const groupApps = sortApps(filtered.filter((a) => a.group === group.id), sort, lastUsed);
          if (groupApps.length === 0) return null;
          return (
            <div key={group.id}>
              <Section title={group.title} sub={group.sub} />
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: `repeat(${group.cols}, 1fr)`,
                  gap: group.cols === 4 ? 10 : 12,
                }}
              >
                {groupApps.map((a) => {
                  const pinned = pins.includes(a.id);
                  return (
                    <AppTile
                      key={a.id}
                      name={a.name}
                      desc={a.desc}
                      iconKey={a.iconKey}
                      tone={a.tone}
                      size={a.size}
                      onTap={() => openApp(a)}
                      onPin={() => togglePin(a)}
                      pinned={pinned}
                      pinDisabled={!pinned && bayFull}
                    />
                  );
                })}
              </div>
            </div>
          );
        })}
        {filtered.length === 0 && (
          <p style={{ color: C.inkSoft, fontFamily: F.body, fontWeight: 600 }}>
            {searching
              ? `No apps match “${query.trim()}”.`
              : "No apps available for your role yet."}
          </p>
        )}
      </div>
    </div>
  );
}
